import { ApolloError, gql, useMutation } from "@apollo/client";
import { useAuth } from "@src/common/hooks/useAuth";
import { useErrorToast } from "@src/common/hooks/useErrorToast";
import { useCourieStore } from "@src/common/lib/store";

const FORCE_MARK_AS_ATTEMPTED = gql`
  mutation ForceMarkAsAttempted(
    $courierId: ID!
    $stopId: ID!
    $exceptionReasonId: ID!
  ) {
    forceMarkAsAttempted(
      courierId: $courierId
      stopId: $stopId
      exceptionReasonId: $exceptionReasonId
    )
  }
`;

interface ForceMarkAsAttemptedData {
  forceMarkAsAttempted: string;
}

interface ForceMarkAsAttemptedVariables {
  courierId: string;
  stopId: string;
  exceptionReasonId: string;
}

interface ForceMarkAsAttemptedArgs {
  stopId: string;
  exceptionReasonId: string;
}

export function useForceMarkAsAttempted() {
  const { courierId } = useAuth();
  const showError = useErrorToast();
  const { showToast } = useCourieStore();

  const [mutate, { loading }] = useMutation<
    ForceMarkAsAttemptedData,
    ForceMarkAsAttemptedVariables
  >(FORCE_MARK_AS_ATTEMPTED);

  function forceMarkAsAttempted(
    args: ForceMarkAsAttemptedArgs
  ): Promise<void> {
    if (!courierId) return Promise.reject(new Error("No courier ID available"));
    return mutate({ variables: { courierId, ...args } })
      .then(() => {
        // Deliberately non-committal: the client cannot see the prior attempt
        // count, so it does not know whether this scheduled a re-attempt or
        // applied the reason's final state to the whole order.
        showToast({
          message: "Attempt recorded. See the order's activity for the outcome.",
          type: "success",
        });
      })
      .catch((err) => {
        showError(err as ApolloError);
        // Rethrown so the caller's .catch keeps the modal open; swallowing
        // here would close it and discard the user's selection.
        throw err;
      });
  }

  return { forceMarkAsAttempted, isRecording: loading };
}
